import React, { useState } from 'react';
import CustomChip from 'components/selects/CustomChip';
import { Code } from 'models/common/CommonCode';
import { useMechUtTypes } from 'hooks/useMechUtTypes';
import { usePrdnProcOrder } from 'hooks/usePrdnProcOrder';
import { useUtComparisonStore } from '../store/useUtComparisonStore';
import { aggregateByProcessEqGroup, calcTotals } from '../utils/analyze';
import { fmt } from '../utils/excelUtils';
import SectionBox from './SectionBox';

type SummaryTab = 'basic' | 'detail';

/** 서브탭 목록 — CustomChip이 공통코드 모양(cmnCd/cmnCdNm)을 받으므로 그 형태로 둔다 */
const TAB_OPTIONS: Code[] = [
  { cmnCd: 'basic', cmnCdNm: '전체 Summary' },
  { cmnCd: 'detail', cmnCdNm: '세부 Summary (공정/설비군/세부공정별)' },
];

/**
 * ⑤ Summary — 전체(버전별 UT 합계) / 세부(공정·설비군·세부공정별).
 *
 * 두 표 모두 사이트 기본 표 스타일(table-outer + styles.ts의 thead th / td)을 쓴다.
 * 전체 Summary는 예전에 PPT 산출물을 흉내낸 별도 배색(.ppt-table)이었는데, 화면 안에서
 * 혼자 튀어 보여 세부 Summary와 같은 모양으로 맞췄다.
 */
const SummarySection = () => {
  const analysisResult = useUtComparisonStore((s) => s.analysisResult);
  const versionLabels = useUtComparisonStore((s) => s.versionLabels);
  const isPerLineMode = useUtComparisonStore((s) => s.isPerLineMode);
  const currentUT = useUtComparisonStore((s) => s.currentUT);
  const [tab, setTab] = useState<SummaryTab>('basic');
  // 조기 반환보다 위에 있어야 훅 순서가 깨지지 않는다
  const utTypes = useMechUtTypes();
  const procOrder = usePrdnProcOrder();

  if (!analysisResult) return null;

  const n = versionLabels.length;
  const totals = calcTotals(analysisResult, n);
  const modeNote = isPerLineMode ? ' (라인당 사용량 합산 기준)' : '';

  // 공정은 이름순이 아니라 공통코드(PRDN_PROC_CD) 순서로 — ④ 표와 같은 규칙
  const groups = aggregateByProcessEqGroup(analysisResult, n, procOrder);
  const utList = currentUT === 'ALL' ? utTypes : utTypes.filter((u) => u.key === currentUT);

  const detailTotals = utList.map((ut) => {
    const usage = Array(n).fill(0);
    for (const g of groups) {
      g.ut[ut.key].usage.forEach((v, i) => (usage[i] += v));
    }
    return { ut, usage };
  });

  const renderDetailCells = (usage: number[], key: string) => {
    const diff = usage[usage.length - 1] - usage[0];
    const pct = usage[0] === 0 ? '-' : ((diff / usage[0]) * 100).toFixed(1) + '%';
    const cls = diff > 0 ? 'diff-pos' : diff < 0 ? 'diff-neg' : 'diff-zero';
    return (
      <React.Fragment key={key}>
        {usage.map((v, i) => (
          <td key={i}>{fmt(v)}</td>
        ))}
        <td className={cls}>
          {diff > 0 ? '+' : ''}
          {fmt(diff)}
        </td>
        <td className={cls}>{diff === 0 ? '-' : (diff > 0 ? '+' : '') + pct}</td>
      </React.Fragment>
    );
  };

  return (
    /* 건수는 ④·⑥처럼 "총 N건"으로 — 지금 보고 있는 표의 데이터 행 수를 센다
       (전체 Summary는 버전 행, 세부 Summary는 공정·설비군·세부공정 조합. 차이/합계 행 제외) */
    <SectionBox
      no="⑤"
      title="Summary"
      count={tab === 'detail' ? groups.length : versionLabels.length}
    >
      <div className="chip-row">
        <CustomChip
          options={TAB_OPTIONS}
          defaultValue={tab}
          singleSelect
          /* 선택된 칩을 다시 눌러 해제되면(빈 값) 기본 탭으로 되돌린다 */
          onChange={(value) => setTab(((value as string) || 'basic') as SummaryTab)}
        />
      </div>

      {tab === 'basic' ? (
        /* 틀·헤더·셀 스타일은 세부 Summary와 같은 것을 쓴다(table-outer + 사이트 기본 규칙).
           total-summary-table은 고정열을 버전 열 하나로 줄이는 용도다 — styles.ts 참고 */
        <div className="table-outer">
          <table className="total-summary-table">
            <thead>
              <tr>
                <th>버전{modeNote}</th>
                {utTypes.map((ut) => (
                  <th key={ut.key}>{ut.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {versionLabels.map((name, vi) => (
                <tr key={vi}>
                  <td>{name}</td>
                  {utTypes.map((ut) => (
                    <td key={ut.key}>{fmt(totals[ut.key][vi])}</td>
                  ))}
                </tr>
              ))}
              <tr className="diff-row">
                <td>차이</td>
                {utTypes.map((ut) => {
                  const arr = totals[ut.key];
                  const diff = arr[arr.length - 1] - arr[0];
                  // 증감 색은 세부 Summary와 같은 클래스를 쓴다 (diff-pos / diff-neg / diff-zero)
                  const cls = diff > 0 ? 'diff-pos' : diff < 0 ? 'diff-neg' : 'diff-zero';
                  return (
                    <td className={cls} key={ut.key}>
                      {diff > 0 ? '+' : ''}
                      {fmt(diff)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="table-outer">
          <table>
            <thead>
              <tr>
                <th rowSpan={2} style={{ left: 0, zIndex: 6 }}>
                  공정
                </th>
                <th rowSpan={2} style={{ left: 0, zIndex: 6 }}>
                  설비군
                </th>
                <th rowSpan={2} style={{ left: 0, zIndex: 6 }}>
                  세부공정
                </th>
                {utList.map((ut) => (
                  <th key={ut.key} colSpan={n + 2} className="group-header">
                    {ut.label}
                  </th>
                ))}
              </tr>
              <tr>
                {utList.map((ut) => (
                  <React.Fragment key={ut.key}>
                    {versionLabels.map((_, vi) => (
                      <th key={vi}>V{vi + 1} 사용량</th>
                    ))}
                    <th>차이</th>
                    <th>증감률</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 ? (
                <tr>
                  <td colSpan={3 + utList.length * (n + 2)} className="empty-state">
                    <p>표시할 데이터가 없습니다.</p>
                  </td>
                </tr>
              ) : (
                <>
                  {groups.map((g) => (
                    <tr key={`${g.process}||${g.eqGroup}||${g.subProcess}`}>
                      <td>{g.process}</td>
                      <td>{g.eqGroup}</td>
                      <td>{g.subProcess}</td>
                      {utList.map((ut) => renderDetailCells(g.ut[ut.key].usage, ut.key))}
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 700, background: '#d9fcf4' }}>
                    <td>합계</td>
                    <td></td>
                    <td></td>
                    {detailTotals.map((t) => renderDetailCells(t.usage, t.ut.key))}
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </SectionBox>
  );
};

export default SummarySection;
